import { useEffect, useState } from "react";
import { cn } from "@ui/lib/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useBotDebug } from "./-use-bot-debug";
import { useAtomValue } from "jotai";
import { AlertCircle, ArrowLeft, Bot, GitBranch, Save, Send, Undo2 } from "lucide-react";
import { Button } from "@tubo/ui/components/ui/button";
import { useUiTranslation } from "@tubo/ui/i18n";
import {
  useListBotWorkspaceSkills,
} from "@tubo/api-models/hooks/chat-bot-api.hooks";
import {
  getListBotsQueryKey,
  getListBotVersionsQueryKey,
  useListBots,
  useListBotVersions,
  useCreateVersion,
  useUpdateBot,
} from "@tubo/api-models/hooks/chat-bot-api.hooks";
import {
  getListBotMarketListingsQueryKey,
  useListBotMarketListings,
} from "@tubo/api-models/hooks/bot-market-api.hooks";
import {
  useDelistTenantBotListing,
  useSubmitBotToMarket,
} from "@tubo/api-models/hooks/bot-submission-api.hooks";
import {
  getListBotRunsQueryKey,
  useListBotRuns,
  useStartBotAgentRun,
} from "@tubo/api-models/hooks/bot-run-api.hooks";
import { authTokenAtom } from "@tubo/store";
import { toDraft, buildUpdateRequest } from "../-studio-draft";
import { useListAgentTools } from "@tubo/api-models/hooks/agent-tool-api.hooks";
import { toast } from "sonner";
import { resourceActionPolicy } from "@client/lib/resource-action-policy";
import { extractErrorMsg } from "@tubo/ui/lib/utils";
import { DebugSidebar } from "./-debug-sidebar";
import { StudioForm, type StudioDraft } from "./-studio-form";
import { ChatResponseLanguageEnum } from "@tubo/api-models/business/ai-chat/chat-response-language.enums";
import type { BotListingVO } from "@tubo/api-models/hooks/common.schemas";

export const Route = createFileRoute("/_app/bots/$botId/studio")({
  component: BotStudioPage,
});

type StudioSaveState = "saved" | "dirty" | "saving";

function findPublishedTenantListing(listings: BotListingVO[] | undefined, botId: string) {
  return (listings ?? []).find((listing) =>
    listing.botId === botId && listing.scope === "tenant" && listing.status === "published",
  );
}

function BotStudioReadOnly({ onBack }: { onBack: () => void }) {
  const { t } = useUiTranslation("botStudio");
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
      <Bot className="size-8 text-muted-foreground" />
      <div>
        <p className="font-medium">{t("route.readOnlyTitle")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t("route.readOnlyDescription")}</p>
      </div>
      <Button variant="outline" onClick={onBack}>
        {t("route.backToAgents")}
      </Button>
    </div>
  );
}

function BotStudioHeader({
  botName,
  draft,
  saveState,
  canAdmin,
  hasPublishedListing,
  isSaving,
  isCreatingVersion,
  isSubmitting,
  isDelisting,
  onBack,
  onSave,
  onCreateVersion,
  onPublish,
  onDelist,
}: {
  botName: string;
  draft: StudioDraft;
  saveState: StudioSaveState;
  canAdmin: boolean;
  hasPublishedListing: boolean;
  isSaving: boolean;
  isCreatingVersion: boolean;
  isSubmitting: boolean;
  isDelisting: boolean;
  onBack: () => void;
  onSave: () => void;
  onCreateVersion: () => void;
  onPublish: () => void;
  onDelist: () => void;
}) {
  const { t } = useUiTranslation("botStudio");
  const isDraftInvalid = !draft.name.trim() || !draft.systemPrompt.trim();

  return (
    <header className="flex min-h-12 shrink-0 flex-wrap items-center gap-2 border-b bg-background px-3 py-2 md:flex-nowrap md:px-4 md:py-0">
      <Button
        variant="ghost"
        size="sm"
        className="px-2 text-muted-foreground hover:text-foreground"
        onClick={onBack}
      >
        <ArrowLeft className="size-4" />
        {t("route.backToAgents")}
      </Button>
      <div className="h-4 w-px bg-border" />
      <div className="flex min-w-0 items-center gap-2.5">
        <p className="truncate text-sm font-semibold">{botName}</p>
        {saveState === "dirty" ? (
          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
            {t("route.draftEditing")}
          </span>
        ) : saveState === "saving" ? (
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {t("route.saving")}
          </span>
        ) : (
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {t("route.saved")}
          </span>
        )}
      </div>
      <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onSave} disabled={isSaving || isDraftInvalid}>
          <Save className="size-4" />
          {t("route.saveDraft")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onCreateVersion}
          disabled={isCreatingVersion || isSaving || isDraftInvalid}
        >
          <GitBranch className="size-4" />
          {t("route.createVersion")}
        </Button>
        {canAdmin && (hasPublishedListing ? (
          <Button variant="outline" size="sm" onClick={onDelist} disabled={isDelisting}>
            <Undo2 className="size-4" />
            {isDelisting ? t("route.delisting") : t("route.delist")}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={onPublish}
            disabled={isCreatingVersion || isSubmitting || isSaving || isDraftInvalid}
          >
            <Send className="size-4" />
            {isSubmitting ? t("route.submitting") : t("route.publish")}
          </Button>
        ))}
      </div>
    </header>
  );
}

function StudioMobileTabs({ active, onChange }: { active: boolean; onChange: (value: boolean) => void }) {
  const { t } = useUiTranslation("botStudio");
  return (
    <div className="flex gap-2 border-b px-4 py-2 @min-[1020px]/studio:hidden">
      <Button variant={!active ? "secondary" : "ghost"} size="sm" onClick={() => onChange(false)}>{t("route.configure")}</Button>
      <Button variant={active ? "secondary" : "ghost"} size="sm" onClick={() => onChange(true)}>{t("route.debug")}</Button>
    </div>
  );
}

function BotStudioPage() {
  const { t } = useUiTranslation("botStudio");
  const { botId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useAtomValue(authTokenAtom);
  const { data: response, isLoading } = useListBots();
  const { data: marketResponse } = useListBotMarketListings();
  const { data: versionResponse } = useListBotVersions(botId);
  const { data: runResponse } = useListBotRuns(botId, {
    query: { refetchInterval: 5_000 },
  });
  const skillsQuery = useListBotWorkspaceSkills();
  const toolsQuery = useListAgentTools();
  const skills = skillsQuery.data;
  const toolsResp = toolsQuery.data;
  const customTools = toolsResp?.tools ?? [];
  const versions = versionResponse?.versions ?? [];
  const runs = runResponse?.runs ?? [];
  const bot = response?.bots?.find((item) => item.id === botId);
  const publishedTenantListing = findPublishedTenantListing(marketResponse?.listings, botId);
  const [draft, setDraft] = useState<StudioDraft | null>(null);
  const [saveState, setSaveState] = useState<StudioSaveState>("saved");
  // 草稿变更检测：最新成功 debug run 的 draftRevision 是否匹配当前草稿
  const latestDebugRun = runs.find((r) => r.mode === "debug" && !r.botVersionId && r.status === "succeeded");
  const draftStale = bot && latestDebugRun && (latestDebugRun.draftRevision ?? 0) !== (bot.draftRevision ?? 0);
  const [debugInput, setDebugInput] = useState("");
  const [debugResponseLanguage, setDebugResponseLanguage] = useState(ChatResponseLanguageEnum.Auto);
  const [mobileDebug, setMobileDebug] = useState(false), [debugFullscreen, setDebugFullscreen] = useState(false);
  const [debugNeedsReset, setDebugNeedsReset] = useState(false);
  const { messages: debugMessages, sendMessage: sendDebugMessage, respondToInput: respondToDebugInput,
    status: debugStatus, setMessages: setDebugMessages, debugMode, setDebugMode, target, selectTarget,
    reset: resetDebug, stop: stopDebug, error: debugError, runtimeLabel } = useBotDebug(botId, token, debugResponseLanguage);

  useEffect(() => {
    if (bot) {
      setDraft(toDraft(bot));
      setSaveState("saved");
    }
  }, [bot]);

  // 表单编辑 → 标记未保存；服务端数据刷新回显不标记
  const updateDraft = (next: StudioDraft) => {
    setDraft(next);
    setSaveState((prev) => (prev === "saving" ? prev : "dirty"));
  };

  const updateMutation = useUpdateBot({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListBotsQueryKey() });
      },
    },
  });

  const submitMutation = useSubmitBotToMarket({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListBotMarketListingsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListBotsQueryKey() });
        toast.success(t("route.published"));
      },
      onError: () => toast.error(t("route.publishFailed")),
    },
  });

  const delistMutation = useDelistTenantBotListing({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListBotMarketListingsQueryKey() });
        toast.success(t("route.delisted"));
      },
      onError: () => toast.error(t("route.delistFailed")),
    },
  });

  const createVersionMutation = useCreateVersion({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListBotVersionsQueryKey(botId) });
      },
    },
  });

  const agentRunMutation = useStartBotAgentRun({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListBotRunsQueryKey(botId) });
        setDebugInput("");
        toast.success(t("route.agentRunQueued"));
      },
      onError: () => {
        toast.error(t("route.agentRunFailed"));
      },
    },
  });

  if (isLoading || !draft) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {t("route.loading")}
      </div>
    );
  }

  if (!bot) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <Bot className="size-8 text-muted-foreground" />
        <div>
          <p className="font-medium">{t("route.notFound")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("route.notFoundDescription")}</p>
        </div>
        <Button variant="outline" onClick={() => navigate({ to: "/bots" })}>
          {t("route.backToAgents")}
        </Button>
      </div>
    );
  }

  const { canEdit, canAdmin } = resourceActionPolicy(bot.effectiveRight);

  if (!canEdit) {
    return <BotStudioReadOnly onBack={() => navigate({ to: "/bots" })} />;
  }

  const saveDraft = async () => {
    if (saveState !== "dirty") return;
    setSaveState("saving");
    try {
      await updateMutation.mutateAsync(buildUpdateRequest(draft, botId));
      toast.success(t("route.draftSaved"));
      setSaveState("saved");
      setDebugNeedsReset(true);
    } catch (error) {
      toast.error(extractErrorMsg(error, t("route.saveFailed")));
      setSaveState("dirty");
    }
  };

  const createVersion = async () => {
    const needsSave = saveState === "dirty";
    setSaveState("saving");
    try {
      if (needsSave) await updateMutation.mutateAsync(buildUpdateRequest(draft, botId));
      await createVersionMutation.mutateAsync({ id: botId, data: {} });
      toast.success(t("route.versionCreated"));
      setSaveState("saved");
    } catch (error) {
      toast.error(extractErrorMsg(error, t("route.createVersionFailed")));
      setSaveState(needsSave ? "dirty" : "saved");
    }
  };

  const submitToMarket = async () => {
    if (!canAdmin) return;
    const needsSave = saveState === "dirty";
    setSaveState("saving");
    try {
      if (needsSave) await updateMutation.mutateAsync(buildUpdateRequest(draft, botId));
      const version = await createVersionMutation.mutateAsync({ id: botId, data: {} });
      setSaveState("saved");
      submitMutation.mutate({ botId, data: { botVersionId: version.id } });
    } catch (error) {
      toast.error(extractErrorMsg(error, t("route.publishFailed")));
      setSaveState(needsSave ? "dirty" : "saved");
    }
  };

  const delistFromMarket = () => {
    if (!canAdmin || !publishedTenantListing) return;
    delistMutation.mutate({ botId, listingId: publishedTenantListing.id, data: {} });
  };

  const startDebug = async () => {
    const content = debugInput.trim();
    if (!content) return;
    if (debugNeedsReset) { await resetDebug(); setDebugNeedsReset(false); }
    if (target === "draft" && saveState === "dirty") {
      await resetDebug();
      setSaveState("saving");
      try {
        await updateMutation.mutateAsync(buildUpdateRequest(draft, botId));
      } catch (error) {
        toast.error(extractErrorMsg(error, t("route.draftSaveFailed")));
        setSaveState("dirty");
        return;
      }
      setSaveState("saved");
    }
    setDebugInput("");
    sendDebugMessage({ text: content });
  };

  return (
    <div className={cn("flex h-full min-w-0 flex-col bg-background", !debugFullscreen && "@container/studio")}>
      <BotStudioHeader
        botName={bot.name}
        draft={draft}
        saveState={saveState}
        canAdmin={canAdmin}
        hasPublishedListing={Boolean(publishedTenantListing)}
        isSaving={updateMutation.isPending}
        isCreatingVersion={createVersionMutation.isPending}
        isSubmitting={submitMutation.isPending}
        isDelisting={delistMutation.isPending}
        onBack={() => navigate({ to: "/bots" })}
        onSave={saveDraft}
        onCreateVersion={createVersion}
        onPublish={submitToMarket}
        onDelist={delistFromMarket}
      />

      {draftStale && (
        <div className="flex items-center gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
          <AlertCircle className="size-4 shrink-0" />
          <span>{t("route.draftStale")}</span>
        </div>
      )}

      {(skillsQuery.isLoading || toolsQuery.isLoading) && <p role="status" className="px-4 py-2 text-sm text-muted-foreground">{t("route.loadingSkillsAndTools")}</p>}
      {(skillsQuery.isError || toolsQuery.isError) && <div role="alert" className="flex items-center gap-2 px-4 py-2 text-sm">
        {t("route.skillsAndToolsLoadFailed")}<Button variant="outline" size="sm" onClick={() => { void skillsQuery.refetch(); void toolsQuery.refetch(); }}>{t("route.reload")}</Button>
      </div>}
      <StudioMobileTabs active={mobileDebug} onChange={setMobileDebug} />
      <div className="flex min-h-0 flex-1">
        <div className={cn("min-w-0 flex-1 @min-[1020px]/studio:flex", mobileDebug ? "hidden" : "flex")}>
        <StudioForm
          botId={botId}
          draft={draft}
          onDraftChange={updateDraft}
          model={bot.model}
          skills={skills ?? []}
          customTools={customTools}
          versions={versions}
        />
        </div>
        <DebugSidebar
          onFullscreenChange={setDebugFullscreen}
          botName={draft.name}
          mobileVisible={mobileDebug}
          versions={versions}
          target={target}
          onTargetChange={selectTarget}
          debugMode={debugMode}
          onDebugModeChange={setDebugMode}
          onStop={() => void stopDebug()}
          error={debugError?.message}
          botId={botId}
          onRespondToInput={respondToDebugInput}
          botType={bot.publishedTaskExecutionEnabled ? "agent" : "chat"}
          debugMessages={debugMessages}
          setDebugMessages={setDebugMessages}
          debugInput={debugInput}
          onDebugInputChange={setDebugInput}
          onStartDebug={startDebug}
          onStartAgentRun={() => { if (debugInput.trim()) agentRunMutation.mutate({ botId, data: { input: debugInput.trim(), responseLanguage: debugResponseLanguage } }); }}
          responseLanguage={debugResponseLanguage}
          onResponseLanguageChange={setDebugResponseLanguage}
          isDebugDisabled={!canEdit || !debugInput.trim() || updateMutation.isPending}
          isStreaming={debugStatus === "streaming" || debugStatus === "submitted"}
          isAgentRunPending={agentRunMutation.isPending}
          onClearContext={resetDebug}
          draftStale={!!draftStale}
          modelName={runtimeLabel}
          runs={runs.filter(run => run.mode === "production")}
        />
      </div>
    </div>
  );
}
